import { enableProdMode, ErrorHandler, Inject, LOCALE_ID, NgModule } from '@angular/core'
import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common'
import { SubjectDisplayNamePipe } from './domain/subject/pipes/subject-display-name.pipe'
import { Nl2brPipe } from './shared/pipes/nl2br.pipe'
import { DisplayDatePipe } from './shared/pipes/display-date.pipe'
import { DisplaySubjectNamePipe } from './domain/subject/pipes/display-subject-name.pipe'
import { DisplayNamePipe } from './domain/subject/pipes/display-name.pipe'
import { FormatIsoDateCzechPipe } from './features/contracts/pipes/format-iso-date-czech.pipe'
import { DisplayHectarePipe } from './domain/parcel/pipes/display-hectare.pipe'
import { DisplayAcreagePipe } from './domain/parcel/pipes/display-acreage.pipe'
import { DisplayVersionTypeTitleTextPipe } from './domain/contract/pipes/display-version-type-title-text.pipe'
import { DisplayPaymentStatePipe } from './domain/payment/pipes/display-payment-state.pipe'
import { PaymentConcernedAutofillComponent } from './features/payments/components/payment-concerned-autofill/payment-concerned-autofill.component'
import { BrowserModule, Title } from '@angular/platform-browser'
import { FormsModule } from '@angular/forms'
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http'
import localeCs from '@angular/common/locales/cs'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { Store, StoreModule } from '@ngrx/store'
import { EffectsModule } from '@ngrx/effects'
import { LoadingBarModule, LoadingBarService } from '@ngx-loading-bar/core'
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError, ActivatedRoute, Params } from '@angular/router'
import { filter } from 'rxjs/operators'
import * as Sentry from '@sentry/angular'
import { AuthApiModule, Configuration as AuthConfiguration, ConfigurationParameters as AuthConfigurationParameters } from './api/auth'
import {
    BackendApiModule,
    Configuration as BackendConfiguration,
    ConfigurationParameters as BackendConfigurationParameters,
} from './api/backend'
import { LoginModule } from './features/login/login.module'
import { AppDialogsModule } from './app-dialogs.module'
import { ConcernedViewStateModule } from './domain/subject/state/concerned-view/concerned-view-state.module'
import { StoreKeysEnum } from './store/store-keys'
import { BuildInfo } from './constants/buildInfo.constant'
import { createBeforeSend } from './core/monitoring/sentry-filters'

import { AuthInterceptorService } from './core/http/auth-interceptor.service'
import { DialogModule } from '@angular/cdk/dialog'
import { ErrorHandlingInterceptorService } from './core/http/error-handling-interceptor.service'
import { MainModule } from './core/layout/main.module'
import { MapStateModule } from './features/map/map-state.module'
import {
    Configuration as RegistrationConfiguration,
    ConfigurationParameters as RegistrationConfigurationParameters,
    RegistrationApiModule,
} from './api/registration'
import { BarterStateModule } from './features/barter/barter-state.module'
import { FlatpickrModule } from 'angularx-flatpickr'
import flatpickr from 'flatpickr'
import { Czech } from 'flatpickr/dist/l10n/cs'
import { IntercomConfigService } from './core/config/intercom-config.service'
import { CommonService } from './core/services/common.service'
import { BreadcrumbsService } from './core/services/breadcrumbs.service'
import { GoogleAnalyticsService } from './core/monitoring/google-analytics.service'
import { perfectScrollbarReducer } from './shared/ui/perfect-scrollbar/state/perfect-scrollbar.reducer'
import { PerfectScrollbarEffects } from './shared/ui/perfect-scrollbar/state/perfect-scrollbar.effects'
import { UserContextModule } from './core/auth/user-context/user-context.module'
import { AppComponent } from './app.component'
import { AppRoutingModule } from './app-routing.module'
import { ImportantMessageComponent } from './core/layout/important-message/important-message.component'
import { AuthModule } from './core/auth/auth.module'
import { Error401Module } from './features/error-401/error-401.module'
import { Error404Module } from './features/error-404/error-404.module'
import { SignedModule } from './features/signed/signed.module'
import { PerfectScrollbarActions } from './shared/ui/perfect-scrollbar/state/actions-type'
import { HeaderModule } from './core/layout/header/header.module'
import { LayoutModule } from './core/layout/layout.module'
import { CommandPaletteModule } from './features/command-palette/command-palette.module'
import { LiberatedAreaStateModule } from './features/admin/codelist/liberated-area/liberated-area-state.module'
import { ProjectStateModule } from './features/admin/project/project-state.module'
import { GeneralMapStateModule } from './domain/general-map/components/general-map/general-map-state.module'

export function authApiConfigFactory(): AuthConfiguration {
    // basePath is resolved from the runtime config, never from the value baked into the
    // generated client (auth-api.yaml `servers.url`, currently '/auth/rest'). The '/auth'
    // prefix is ingress-only routing — ca-auth itself serves plain '/rest' (locally
    // authUrl='http://localhost:8070') — so the definition must not drive the base path.
    const params: AuthConfigurationParameters = {
        withCredentials: true,
        basePath: CA_CONFIG.BACKEND_OPTIONS.authUrl + '/rest',
    }
    return new AuthConfiguration(params)
}

export function backendApiConfigFactory(): BackendConfiguration {
    const params: BackendConfigurationParameters = {}
    return new BackendConfiguration(params)
}

export function registrationApiConfigFactory(): RegistrationConfiguration {
    const params: RegistrationConfigurationParameters = {}
    return new RegistrationConfiguration(params)
}

// Enable production mode for production and staging environments (based on CA_CONFIG.ANGULAR_MODE_ENV)
// - production: real production environment
// - staging: dev, docker, uitest environments (behave like production for testing)
if (BuildInfo.angularModeEnv === 'production' || BuildInfo.angularModeEnv === 'staging') {
    enableProdMode()
    console.log(`App is running in production mode (${BuildInfo.angularModeEnv})`)
} else {
    console.log('App is running in development mode')
}

// Initialize Sentry for production and staging environments
// (staging includes dev, docker, uitest - for catching errors during testing)
//
// DEV-733: E2E prostředí se do Sentry nehlásí — jsou to roboti, ne uživatelé, a jejich
// chyby ředí produkční signál i kvótu. Pozná se podle hostname (<pipeline-id>.e2e.dev.aws…),
// NE podle CA_CONFIG.ENVIRONMENT: build:docker sdílí stejný image i s testovacími
// instancemi a dev prostředím, kde chyby vidět chceme.
const isE2eEnvironment = window.location.hostname.includes('.e2e.')

if ((BuildInfo.angularModeEnv === 'production' || BuildInfo.angularModeEnv === 'staging')
    && !isE2eEnvironment) {
    Sentry.init({
        dsn: 'https://80d1e42dbd9a47f2a865ee8ccc9b584b@o46180.ingest.sentry.io/5666184',
        release: BuildInfo.appVersion,
        attachStacktrace: true,
        // @ts-ignore
        environment: CA_CONFIG.ENVIRONMENT,
        integrations: [
            // Browser tracing for performance monitoring
            Sentry.browserTracingIntegration(),
            // Session replay for reproducing errors (records DOM, not video)
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        // Trace propagation targets (replaces tracingOrigins in v8)
        tracePropagationTargets: ['app.cleverassets.cz', /^\//],
        tracesSampleRate: 0.01,
        // Session replay: capture 10% of sessions, 100% of sessions with errors
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        // DEV-733: HTTP chyby se fingerprintují podle status+endpoint a dedupují
        // v pětiminutovém okně, aby rolling restart backendu nezaplavil Sentry
        // DEV-3143: přihlášený uživatel = nastavený Sentry user (SentryService.loginUser / logoutUser)
        beforeSend: createBeforeSend({ isLoggedIn: (): boolean => !!Sentry.getCurrentScope().getUser()?.id }),
        ignoreErrors: [
            'Non-Error exception captured',
            // @TODO: remove this after update OL to version without bug
            // https://github.com/openlayers/openlayers/pull/16260
            'Cannot read properties of undefined (reading \'visible\')',
        ],
    })
}

@NgModule({
    imports: [
        BrowserModule,
        // App-wide modal dialogs (dissolved from CommonAppModule + CommonDialogsModule — DEV-972)
        AppDialogsModule,
        ConcernedViewStateModule,
        StoreModule.forRoot({ [StoreKeysEnum.PerfectScrollbarComponent]: perfectScrollbarReducer }, {
            runtimeChecks: {
                // strictActionWithinNgZone: true,
                strictStateImmutability: true, // Ensures state is immutable
                strictActionImmutability: true, // Ensures actions are immutable
                strictStateSerializability: true, // Ensures state is serializable
                strictActionSerializability: true, // Ensures actions are serializable
                strictActionWithinNgZone: true, // Ensures actions are dispatched within Angular's zone
                strictActionTypeUniqueness: true, // Ensures action types are unique
            },
        }),
        EffectsModule.forRoot([PerfectScrollbarEffects]),
        StoreDevtoolsModule.instrument({ maxAge: 84, logOnly: BuildInfo.angularModeEnv === 'production' }),
        FormsModule,
        HttpClientModule,
        AppRoutingModule,
        DialogModule,
        // 3rd Party Libs
        FlatpickrModule.forRoot(),
        LoadingBarModule,
        // CA Core
        AuthModule,
        UserContextModule,
        AuthApiModule.forRoot(authApiConfigFactory),
        BackendApiModule.forRoot(backendApiConfigFactory),
        RegistrationApiModule.forRoot(registrationApiConfigFactory),
        LayoutModule,
        MainModule,
        // CA Features
        LoginModule,
        // NgRx state modules (eagerly imported to register store/effects globally)
        BarterStateModule,
        MapStateModule,
        Error401Module,
        Error404Module,
        SignedModule,
        HeaderModule,
        CommandPaletteModule,
        // Global NgRx state modules
        GeneralMapStateModule,
        LiberatedAreaStateModule,
        ProjectStateModule,
    ],
    declarations: [
        AppComponent,
        ImportantMessageComponent,
    ],
    bootstrap: [AppComponent],
    providers: [
        // Sentry ErrorHandler for Angular integration (captures unhandled exceptions)
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,  // Don't show error dialog to users
            }),
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorHandlingInterceptorService,
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useValue: 'cs',
        },
        DatePipe,
        DecimalPipe,
        Nl2brPipe,
        DisplayDatePipe,
        DisplaySubjectNamePipe,
        DisplayNamePipe,
        FormatIsoDateCzechPipe,
        DisplayHectarePipe,
        DisplayAcreagePipe,
        DisplayVersionTypeTitleTextPipe,
        DisplayPaymentStatePipe,
        SubjectDisplayNamePipe,
        PaymentConcernedAutofillComponent,
        // @ts-ignore
        { provide: 'GOOGLE_ANALYTICS_ID', useValue: CA_CONFIG.GOOGLEANALYTICSKEY },
        // @ts-ignore
        { provide: 'BUILD_INFO', useValue: { appVersion: CA_CONFIG.VERSION, buildDate: CA_CONFIG.BUILDDATE } },
    ],
})

export class AppModule {

    constructor(
        @Inject(Router) private angularRouter: Router,
        @Inject(ActivatedRoute) private activatedRoute: ActivatedRoute,
        @Inject(Store) private store: Store,
        @Inject(BreadcrumbsService) private breadcrumbsService: BreadcrumbsService,
        @Inject(CommonService) private commonService: CommonService,
        @Inject(GoogleAnalyticsService) private googleAnalyticsService: GoogleAnalyticsService,
        @Inject(BackendConfiguration) private backendConfiguration: BackendConfiguration,
        @Inject(IntercomConfigService) private intercomConfigService: IntercomConfigService,
        @Inject(RegistrationConfiguration) private registrationConfiguration: RegistrationConfiguration,
        @Inject(Title) private titleService: Title,
        @Inject(LoadingBarService) private loadingBar: LoadingBarService,
    ) {
        // @ts-ignore
        this.backendConfiguration.basePath = CA_CONFIG.BACKEND_OPTIONS.restUrl
        // @ts-ignore
        this.registrationConfiguration.basePath = CA_CONFIG.BACKEND_OPTIONS.azuRestUrl

        registerLocaleData(localeCs, 'cs')
        flatpickr.localize(Czech)

        this.intercomConfigService.config()

        // ==> Legacy hash URL handling (#!/landingPage, #!/contracts/list)
        // Users may have old bookmarks with hash URLs - redirect them to Angular Router
        this.handleLegacyHashUrls()

        // ==> Angular Router event handling (replaces UI-Router transition hooks)
        const loader = this.loadingBar.useRef()
        let previousUrl = ''

        // Snapshot of breadcrumbs before navigation - effects/resolvers can fire between
        // NavigationStart and NavigationEnd, corrupting breadcrumbs via setCurrentBreadcrumbTitle
        let breadcrumbsBeforeNavigation: any[] = []

        // NavigationStart: Start loading bar, clear tooltips, set default title
        this.angularRouter.events.pipe(
            filter(event => event instanceof NavigationStart)
        ).subscribe(() => {
            breadcrumbsBeforeNavigation = (this.breadcrumbsService.getBreadcrumbs() || [])
                .map(bc => ({...bc, properties: {...(bc.properties || {})}}))
            loader.start()
            this.titleService.setTitle('CleverAssets')
            this.commonService.clearHangingTooltips()
        })

        // NavigationEnd: Complete loading bar, breadcrumbs, analytics, scrollbar reset
        this.angularRouter.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            loader.complete()

            // Reset scrollbar cache when navigating to different parent route
            const currentParent = event.url.split('/').slice(0, 3).join('/')
            const previousParent = previousUrl.split('/').slice(0, 3).join('/')
            if (currentParent !== previousParent) {
                this.store.dispatch(PerfectScrollbarActions.resetCache())
            }
            previousUrl = event.url

            // Google Analytics pageview
            setTimeout(() => {
                this.googleAnalyticsService.logPageview(event.url, this.titleService.getTitle())
            }, 500)

            // Special routes that should clear breadcrumbs.
            // Compare the path only — a stray fragment/query (e.g. a legacy bookmark
            // '/#/login' surviving as '/landingPage#/login') must not bypass the clear
            // and let the route's breadcrumb data resurface (DEV-796).
            const path = event.url.split('#')[0].split('?')[0]
            if (path === '/landingPage' || path === '/login' || path === '/changePassword' || path === '/') {
                this.breadcrumbsService.deleteAllBreadcrumbs()
                return
            }

            // Build breadcrumbs from route hierarchy
            const newBreadcrumbs = this.buildBreadcrumbsFromRouteTree(this.activatedRoute.root, '', [], null)

            // DEV-1656: crumb.url is a deliberately lossy back-link (buildReturnableQueryString drops
            // context params so an ancestor crumb cannot re-trigger their side effects). Returning to
            // THIS page must stay lossless though — without ?appendixToContract=… the edit page loses
            // its identity and reopens a rozpracovaný dodatek as a blank contract. Keep the real url
            // aside; breadcrumb links prefer it, ancestors rebuilt from the route tree fall back to url.
            if (newBreadcrumbs.length > 0) {
                newBreadcrumbs[newBreadcrumbs.length - 1].returnUrl = event.urlAfterRedirects
            }

            // Preserve existing breadcrumbs and append only the leaf (last) breadcrumb when:
            // - keepBreadcrumbs=true query param (e.g., Subject detail → Parcels/Map)
            // - type=dialog query param (e.g., Contract → Subject detail, in-context navigation)
            const keepBreadcrumbs = event.url.includes('keepBreadcrumbs=true')
                || event.url.includes('type=dialog')
            if (keepBreadcrumbs) {
                // Use breadcrumbs snapshot from NavigationStart (not current state which may be
                // corrupted by effects/resolvers that fire between NavigationStart and NavigationEnd)
                const leafBreadcrumb = newBreadcrumbs.length > 0 ? [newBreadcrumbs[newBreadcrumbs.length - 1]] : []
                this.breadcrumbsService.setBreadcrumbs([...breadcrumbsBeforeNavigation, ...leafBreadcrumb])

                // Effects/resolvers may have already set the breadcrumb title during navigation
                // (before NavigationEnd) via setCurrentBreadcrumbTitle which also updates page title.
                // Re-apply the title to the correct (last) breadcrumb.
                const pageTitle = this.titleService.getTitle().replace(' - CleverAssets', '')
                if (pageTitle && pageTitle !== 'CleverAssets' && leafBreadcrumb.length > 0 && !leafBreadcrumb[0].title) {
                    this.breadcrumbsService.setCurrentBreadcrumbTitle(pageTitle)
                }
            } else {
                // DEV-797: A full rebuild regenerates ancestor crumbs from the route tree, so an
                // ancestor like "Pachtovní smlouvy" → /contracts loses the filter query state of the
                // list the user came from. Carry it over from the pre-navigation snapshot.
                this.preserveAncestorQueryParams(newBreadcrumbs, breadcrumbsBeforeNavigation)
                this.breadcrumbsService.setBreadcrumbs(newBreadcrumbs)
            }
        })

        // NavigationCancel & NavigationError: Complete loading bar
        this.angularRouter.events.pipe(
            filter(event => event instanceof NavigationCancel || event instanceof NavigationError)
        ).subscribe(() => {
            loader.complete()
        })
    }

    ngDoBootstrap() {
    }

    /**
     * Build breadcrumbs from Angular Router state (source of truth)
     * Traverses route tree and creates breadcrumb for each level with breadcrumb data
     * This prevents reference mutations and is the Angular-standard approach
     *
     * @param route - Current route in traversal
     * @param url - Accumulated URL path
     * @param breadcrumbs - Array of breadcrumb objects
     * @param parentBreadcrumb - Last breadcrumb from parent (used when child resets but has no own breadcrumb)
     */
    private buildBreadcrumbsFromRouteTree(
        route: ActivatedRoute,
        url: string = '',
        breadcrumbs: any[] = [],
        parentBreadcrumb: { title: string; url: string } | null = null
    ): any[] {
        // Get breadcrumb title from route CONFIG (not snapshot.data which inherits from parent!)
        // route.routeConfig.data contains ONLY data defined on THIS route, not merged from parents
        const breadcrumbTitle = route.routeConfig?.data?.['breadcrumb']

        // Build URL from route segments (accumulate path)
        const routePath = route.snapshot.url.map(segment => segment.path).join('/')

        if (routePath) {
            url += `/${routePath}`
        } else if (route.routeConfig?.path && !route.routeConfig.path.startsWith(':') && route.routeConfig.path !== '') {
            // Lazy loaded parent route without segments - use path from config
            // Skip parametrized routes (:id) and empty paths
            url += `/${route.routeConfig.path}`
        }

        // Get route params (merge all params from parent to child)
        const params = route.snapshot.params

        // Track this route's breadcrumb for passing to children
        let currentBreadcrumb = parentBreadcrumb

        // Check for resetBreadcrumbs flag - if true, clear all previous breadcrumbs
        const resetBreadcrumbs = route.routeConfig?.data?.['resetBreadcrumbs']
        if (resetBreadcrumbs) {
            breadcrumbs = []
            // Clear properties cache to prevent tab selection from persisting
            // across different navigation contexts (e.g., going from edit to list to new contract)
            this.breadcrumbsService.clearPropertiesCache()

            // If this route has no breadcrumb but parent had one, use parent's breadcrumb
            // This handles list routes that reset breadcrumbs but should still show module name
            if (breadcrumbTitle === undefined && parentBreadcrumb) {
                // Restore properties from cache (survives navigation)
                const cachedProperties = this.breadcrumbsService.getCachedPropertiesForUrl(url)
                breadcrumbs.push({
                    title: parentBreadcrumb.title,
                    url: url,  // Use current URL (e.g., /contracts/list)
                    state: url,
                    params: params,
                    properties: { ...cachedProperties }
                })
            }
        }

        // Add breadcrumb if this route level has breadcrumb data DIRECTLY defined (not inherited)
        // Include breadcrumbs with empty titles (will be set dynamically by effects)
        if (url && breadcrumbTitle !== undefined) {
            // Restore properties from cache (survives navigation, e.g., selected tab)
            const cachedProperties = this.breadcrumbsService.getCachedPropertiesForUrl(url)
            breadcrumbs.push({
                title: breadcrumbTitle,
                url: url,
                state: url,
                params: params,
                properties: { ...cachedProperties }
            })
            // Track this breadcrumb for children
            currentBreadcrumb = { title: breadcrumbTitle, url: url }
        }

        // Recurse for child routes
        if (route.firstChild) {
            return this.buildBreadcrumbsFromRouteTree(route.firstChild, url, breadcrumbs, currentBreadcrumb)
        }

        // DEV-797: At the leaf (current page) attach returnable query params to its breadcrumb url,
        // so navigating back via breadcrumb restores the original filter (e.g. setFilter=invalid from
        // the "Změny v katastru" landing tile). Only whitelisted filter params are kept
        // (see buildReturnableQueryString).
        if (breadcrumbs.length > 0) {
            const queryString = this.buildReturnableQueryString(route.snapshot.queryParams)
            if (queryString) {
                const leaf = breadcrumbs[breadcrumbs.length - 1]
                leaf.url = leaf.url + queryString
                leaf.state = leaf.url
            }
        }

        return breadcrumbs
    }

    /**
     * DEV-797: Build a query string of "returnable" params for a breadcrumb url.
     * Whitelists only filter-restoration params — action/context params (owner, task,
     * appendixToContract, source*, version, parcels, …) must NOT persist into a back-link,
     * otherwise clicking an ancestor crumb could re-trigger their side effects.
     */
    private buildReturnableQueryString(queryParams: Params): string {
        const returnableParams = ['setFilter', 'resetFilter', 'expirationDate', 'q']
        const entries = Object.entries(queryParams).filter(([key]) => returnableParams.includes(key))
        if (!entries.length) {
            return ''
        }
        const searchParams = new URLSearchParams()
        entries.forEach(([key, value]) => searchParams.set(key, String(value)))
        return `?${searchParams.toString()}`
    }

    /**
     * DEV-797: On a full breadcrumb rebuild an ancestor crumb (e.g. "Pachtovní smlouvy" → /contracts)
     * loses the filter query state of the list the user actually came from
     * (e.g. /contracts/list?setFilter=invalid). Carry that state over from the pre-navigation snapshot,
     * but only for STRICT ancestors — so a fresh navigation to the list itself is not polluted by a
     * stale filter from a previous visit. Snapshot urls only carry whitelisted filter params.
     */
    private preserveAncestorQueryParams(newBreadcrumbs: any[], previousBreadcrumbs: any[]): void {
        newBreadcrumbs.forEach(crumb => {
            const crumbPath = crumb.url.split('?')[0]
            const previous = previousBreadcrumbs.find(prev => {
                const [prevPath, prevQuery] = prev.url.split('?')
                return prevQuery && prevPath.startsWith(`${crumbPath}/`)
            })
            if (previous) {
                crumb.url = previous.url
                crumb.state = previous.url
            }
        })
    }

    /**
     * Handle legacy hash URLs (#!/landingPage, #!/contracts/list)
     * Users may have old bookmarks - redirect them to Angular Router
     */
    private handleLegacyHashUrls(): void {
        const hash = window.location.hash
        if (hash && hash.includes('!/')) {
            let path = hash.replace('#!/', '').replace('!/', '')
            // Old URLs for parcel/contract/subject details are redirected to lists
            path = path.replace(/\/(show|edit|create)\/[\d]+/, '/list')
            // Convert dot notation to slash (contracts.list -> /contracts/list)
            if (path.includes('.')) {
                path = '/' + path.replaceAll('.', '/')
            } else if (!path.startsWith('/')) {
                path = '/' + path
            }
            // Clear the hash and navigate to Angular Router
            window.history.replaceState(null, '', window.location.pathname)
            this.angularRouter.navigateByUrl(path)
        }
    }

}